import { call, put, race, take, takeLatest } from 'redux-saga/effects';
import { eventChannel, buffers } from 'redux-saga';
import axios from 'axios';
import { REQUEST, CLEAR, fetchSuccess, fetchFailure } from '../../actions/datasources/datasetInspect';
import config from '../../../config';
import { getLocalUser } from '../../../utils/localUser';
import { Error as ErrorAction } from '../../actions/error/error';

// Stage a multi-file dataset ONE file per request (each stays inside the same
// body-size envelope as a single-file upload), then send the staged
// descriptors to /datasets/inspect for the one-shot AI plan. Upload progress
// is streamed back through the slice via an event channel.
const createUploadChannel = (url, form, headers) => eventChannel((emit) => {
  axios.post(url, form, {
    headers,
    onUploadProgress: (ev) => emit({ progress: ev }),
  })
    .then((response) => emit({ response }))
    .catch((error) => emit({ error }));
  return () => {};
}, buffers.expanding(4));

function* operation(action) {
  const files = action.payload.files;
  let currentName = files[0]?.name;
  try {
    const staged = [];
    // One monotonic progress bar for the WHOLE batch: weight each file by
    // its byte size and accumulate, so finishing file 3 and starting file 4
    // moves the bar forward instead of snapping back to zero.
    const totalBytes = files.reduce((sum, f) => sum + (f.size || 0), 0) || 1;
    let uploadedBytes = 0;
    let progressFloor = 0;
    for (let i = 0; i < files.length; i += 1) {
      const f = files[i];
      currentName = f.name;
      const baseBytes = uploadedBytes;
      const overallPct = (extra) => Math.min(99, Math.round(((baseBytes + extra) * 100) / totalBytes));
      progressFloor = Math.max(progressFloor, overallPct(0));
      yield put(fetchSuccess({
        status: 'staging', count: files.length, index: i + 1, current: f.name, progress: progressFloor,
      }));
      const form = new FormData();
      form.append('file', f);
      const channel = yield call(
        createUploadChannel,
        `${config.API_URL}/ingestion/connect/datasets/stage`,
        form,
        { ...getLocalUser(), 'Content-Type': 'multipart/form-data' },
      );
      let response = null;
      while (!response) {
        const ev = yield take(channel);
        if (ev.progress) {
          const loaded = Math.min(ev.progress.loaded || 0, f.size || ev.progress.loaded || 0);
          progressFloor = Math.max(progressFloor, overallPct(loaded));
          yield put(fetchSuccess({
            status: 'staging', count: files.length, index: i + 1, current: f.name, progress: progressFloor,
          }));
        } else if (ev.error) {
          throw ev.error;
        } else {
          response = ev.response;
        }
      }
      const data = response.data?.data || response.data;
      staged.push(data.file);
      uploadedBytes += f.size || 0;
    }
    currentName = null;
    yield put(fetchSuccess({ status: 'planning', count: files.length }));
    const res = yield call(
      axios.post,
      `${config.API_URL}/ingestion/connect/datasets/inspect`,
      { files: staged },
      { headers: getLocalUser() },
    );
    const data = res.data?.data || res.data;
    yield put(fetchSuccess({ status: 'ready', dataset_id: data.dataset_id, plan: data.plan }));
  } catch (error) {
    yield put(ErrorAction(error));
    const msg = error.response?.data?.error || error.message || 'Could not analyze the files.';
    yield put(fetchFailure(currentName ? `${currentName}: ${msg}` : msg));
  }
}

// CLEAR aborts an in-flight staging run (mirrors the component unmounting).
function* run(action) {
  yield race([call(operation, action), take(CLEAR)]);
}

export function* watch() {
  yield takeLatest(REQUEST, run);
}
